import React, { useState } from "react";
import { getExchangeValues } from "@/components/Sidebar/getExchangeValues";
import { useTradesPagination, AuditTradeItemClass, useSearch, useProfile } from "@moonscape/shared";
import { ArrowsRightLeftIcon, MagnifyingGlassIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyWidget } from "@/components/ui/empty-widget";
import { TransactionAuditModal } from "@/screens/TransactionAuditScreen/TransactionAuditModal";
import { TransactionListContext } from "@/contexts";
import { SpamBadge } from "@/components/SpamBadge";
import { CoinMark, DHead, Fig, Pill, Tag, TagTone } from "@/components/ms";
import { HeadLink } from "./chrome";

/** Buys read positive, sells stay neutral, anything odd goes tan. */
const typeTone = (type?: string): TagTone => {
  const t = (type || "").toLowerCase();
  if (t.includes("buy") || t.includes("deposit") || t.includes("receive")) return "pos";
  if (t.includes("sell") || t.includes("withdrawal") || t.includes("send")) return "neu";
  return "neu";
};

export const RecentTransactionsWidget = () => {
  const { profile } = useProfile();
  // Search state - use the same useSearch hook as TradesScreen
  const {
    isLoading: isSearchLoading,
    isSearching,
    searchedValue,
    onLoadingHandler,
    onChangeHandler,
  } = useSearch();

  const {
    trades,
    isQueryLoading,
    previousPagination,
    nextPagination,
    resetPagination,
    hasMore,
    page,
    isPreviousData,
    isFetching,
  } = useTradesPagination({
    exchange: null,
    asset: null,
    isSearching,
    searchedValue,
    onLoading: () => {},
  });

  // Get transactions (show all from current page when searching/paginating)
  const recentTxns = (isSearching || page > 1) ? (trades?.items || []) : (trades?.items?.slice(0, 5) || []);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTxnId, setSelectedTxnId] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isRefresh, setIsRefresh] = useState(false);

  const handleTransactionClick = (extTxnId: string) => {
    setSelectedTxnId(extTxnId);
    setIsModalOpen(true);
  };

  const searchField = (
    <div className="relative mb-4 mt-3.5">
      <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ms-dim" />
      <input
        type="search"
        name="transaction-search"
        autoComplete="off"
        spellCheck="false"
        placeholder="Search assets, exchanges, amounts, dates…"
        onChange={onChangeHandler}
        onFocus={() => {
          if (page !== 1) {
            resetPagination();
          }
        }}
        className="h-11 w-full border border-ms-line bg-ms-card pl-10 pr-3 font-mono text-[12.5px] text-ms-ink transition-colors placeholder:text-ms-dim focus:border-ms-line2 focus:outline-none"
      />
    </div>
  );

  // Loading state
  if (isQueryLoading) {
    return (
      <section className="flex flex-col">
        <DHead label="Recent activity" right={<HeadLink to="/transactions">See all</HeadLink>} />
        {isSearching && searchField}
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="flex items-center gap-3 border-t border-ms-line py-[11px] first:border-t-0">
            <Skeleton circle width={30} height={30} />
            <Skeleton className="h-4 flex-1" />
            <Skeleton className="h-4 w-20" />
          </div>
        ))}
      </section>
    );
  }

  // Empty state (not while a page/search fetch is in flight — chrome must hold)
  if (recentTxns.length === 0 && !isFetching) {
    return (
      <section className="flex flex-col">
        <DHead label="Recent activity" />
        {!profile?.taxJurisdiction ? (
          <EmptyWidget
            icon={<ExclamationTriangleIcon className="h-8 w-8 text-ms-tan" />}
            title="Complete your tax setup"
            description="Set your tax country and accounting method to see your transactions"
            action={{ label: "Complete Setup", to: "/quiz" }}
          />
        ) : getExchangeValues(profile?.linkedExchangeAccounts).length ? (
          <EmptyWidget
            icon={<ArrowsRightLeftIcon className="h-8 w-8" />}
            title="Waiting for your first import"
            description="Your source is connected — transactions land here as soon as the first import finishes"
            action={{ label: "View sources", to: "/integrations" }}
          />
        ) : (
          <EmptyWidget
            icon={<ArrowsRightLeftIcon className="h-8 w-8" />}
            title="Connect to see your activity"
            description="Add an exchange or wallet to track your transactions"
            action={{ label: "Connect Wallet", to: "/settings/exchange/new" }}
          />
        )}
      </section>
    );
  }

  return (
    <section className="flex flex-col">
      <DHead
        label="Recent activity"
        right={<HeadLink to="/transactions">See all ({trades?.total || 0})</HeadLink>}
      />

      {searchField}

      <div className="flex flex-col">
      {isFetching
        ? // Skeletons rendered through Fig itself so row height/padding/borders
          // match the real rows exactly — the bars sit inside Fig's own text
          // line boxes (inline-block, shorter than the line-height) so they
          // can't change the line height.
          Array.from({ length: recentTxns.length || 5 }, (_, i) => (
            <Fig
              key={i}
              lead={<Skeleton circle width={30} height={30} />}
              label={<Skeleton className="inline-block h-[10px] w-28 align-middle" />}
              sub={<Skeleton className="inline-block h-[7px] w-36 align-middle" />}
              value={
                <>
                  <Skeleton className="inline-block h-[10px] w-24 align-middle" />
                  <span className="mt-[2px] block text-[10px] font-normal">
                    <Skeleton className="inline-block h-[8px] w-16 align-middle" />
                  </span>
                </>
              }
            />
          ))
        : recentTxns.map((txn, index) => {
        // Use AuditTradeItemClass to properly format transaction data
        const t = new AuditTradeItemClass(txn);
        const displayData = t.getDisplayData();

        // Use centralized methods from AuditTradeItemClass
        const coinSymbol = t.getCoinSymbol();
        const assetName = t.getDisplayAssetName();
        const primaryAmount = t.getDisplayAmount();
        const counterAmount = t.getDisplayCounterAmount();

        return (
          <Fig
            key={`${txn.id}-${index}`}
            onClick={() => handleTransactionClick(txn.ext_txn_id)}
            lead={<CoinMark sym={coinSymbol} />}
            label={assetName}
            sub={
              <>
                {displayData.exchangeName} · {t.formattedDate()}
                {txn.spam_category ? (
                  <SpamBadge
                    category={txn.spam_category}
                    note={txn.txn_note}
                    className="ml-2 !px-1 !py-0 !text-[9px]"
                  />
                ) : null}
              </>
            }
            tag={txn.type ? <Tag tone={typeTone(txn.type)}>{String(txn.type).toUpperCase()}</Tag> : undefined}
            value={
              <>
                {primaryAmount}
                {counterAmount ? (
                  <span className="mt-[2px] block text-[10px] font-normal text-ms-dim">
                    {counterAmount}
                  </span>
                ) : null}
              </>
            }
          />
        );
      })}
      </div>

      {/* Pagination - Show when searching or on pages > 1 */}
      {(isSearching || page > 1 || hasMore) && (
        <div className="mt-4 flex justify-center gap-3 sm:justify-end">
          <Pill
            variant="ghost"
            onClick={previousPagination}
            disabled={page === 1 || isPreviousData}
            className="min-h-[44px] px-6 text-[13px] disabled:cursor-not-allowed disabled:opacity-50"
          >
            Previous
          </Pill>
          <Pill
            variant="ghost"
            onClick={nextPagination}
            disabled={!hasMore || isPreviousData}
            className="min-h-[44px] px-6 text-[13px] disabled:cursor-not-allowed disabled:opacity-50"
          >
            Next
          </Pill>
        </div>
      )}

      {/* Transaction Detail Modal */}
      {selectedTxnId && (
        <TransactionListContext.Provider
          value={{
            setModalOpen: setIsModalOpen,
            setExtnTxnId: setSelectedTxnId,
            isEditing,
            setIsEditing,
            isRefresh,
            setIsRefresh,
          }}
        >
          <TransactionAuditModal
            open={isModalOpen}
            setOpen={setIsModalOpen}
            extTxnId={selectedTxnId}
            page="dashboard"
          />
        </TransactionListContext.Provider>
      )}
    </section>
  );
};
